import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { PublicBookShell } from '@/components/booking/PublicBookShell'
import { useUiLocale } from '@/contexts/UiLocaleContext'
import {
  cancelPublicBooking,
  fetchPublicBookingManage,
  reschedulePublicBooking,
} from '@/lib/api/publicBooking'
import { formatBookWhen } from '@/lib/bookingDisplay'

export function BookManagePage() {
  const { token } = useParams()
  const { locale, t } = useUiLocale()
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState('')
  const [rescheduleAt, setRescheduleAt] = useState('')

  useEffect(() => {
    if (!token) {
      return
    }

    fetchPublicBookingManage(token)
      .then((payload) => {
        setData(payload)
        setRescheduleAt(payload.appointment.starts_at?.slice(0, 16).replace('T', ' ') ?? '')
      })
      .catch(() => setError(t('publicBook.manage.expired')))
  }, [token, t])

  async function handleCancel() {
    if (!token || !window.confirm(t('publicBook.manage.confirmCancel'))) {
      return
    }

    setBusy('cancel')
    setMessage('')
    setError('')

    try {
      const result = await cancelPublicBooking(token)
      setMessage(result.message)
      setData((prev) => ({
        ...prev,
        appointment: { ...prev.appointment, status: result.data.status },
        policies: { ...prev.policies, can_cancel: false, can_reschedule: false },
      }))
    } catch (err) {
      setError(err?.response?.data?.message ?? t('publicBook.manage.errorCancel'))
    } finally {
      setBusy('')
    }
  }

  async function handleReschedule(event) {
    event.preventDefault()

    if (!token || !rescheduleAt) {
      return
    }

    setBusy('reschedule')
    setMessage('')
    setError('')

    try {
      const result = await reschedulePublicBooking(token, {
        starts_at: rescheduleAt.replace('T', ' ').slice(0, 19),
      })
      setMessage(result.message)
      setData((prev) => ({
        ...prev,
        appointment: {
          ...prev.appointment,
          starts_at: result.data.starts_at,
          status: result.data.status,
        },
      }))
    } catch (err) {
      const fieldError = err?.response?.data?.errors?.starts_at?.[0]
      setError(fieldError ?? err?.response?.data?.message ?? t('publicBook.manage.errorReschedule'))
    } finally {
      setBusy('')
    }
  }

  if (error) {
    return (
      <PublicBookShell outerClassName="bg-[#E4D7BF]" innerClassName="bg-[#F0E3CC]">
        <div className="mx-auto flex min-h-[100dvh] max-w-md flex-col justify-center px-6 pt-10 text-center">
          <p className="text-sm text-red-600">{error}</p>
          <Link to="/book" className="mt-4 text-sm font-semibold text-[#1F1611] underline">
            {t('publicBook.manage.backHub')}
          </Link>
        </div>
      </PublicBookShell>
    )
  }

  if (!data) {
    return (
      <PublicBookShell outerClassName="bg-[#E4D7BF]" innerClassName="bg-[#F0E3CC]">
        <div className="flex min-h-[100dvh] items-center justify-center pt-10 text-sm text-[#8B7E6E]">
          {t('publicBook.manage.loading')}
        </div>
      </PublicBookShell>
    )
  }

  const { appointment, policies } = data
  const isCancelled = appointment.status === 'cancelled'

  return (
    <PublicBookShell outerClassName="bg-[#E4D7BF]" innerClassName="bg-[#F0E3CC]">
      <div className="mx-auto min-h-[100dvh] max-w-md px-4 pb-8 pt-14">
        <Link to="/book" className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-[#8B7E6E]">
          <span className="book-rtl-flip">←</span>
          {t('publicBook.manage.allBranches')}
        </Link>

        <h1 className="mt-4 text-2xl font-bold text-[#1F1611]">{t('publicBook.manage.title')}</h1>
        <p className="mt-1 text-sm text-[#8B7E6E]">{appointment.branch_name}</p>

        <div className="mt-6 rounded-3xl border border-[#ECE3D0] bg-[#FBF6EC] p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#8B7E6E]">{t('publicBook.manage.when')}</p>
          <p className="mt-1 text-lg font-semibold text-[#1F1611]">
            {formatBookWhen(appointment.starts_at, locale)}
          </p>

          <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-[#8B7E6E]">{t('publicBook.manage.guest')}</p>
          <p className="mt-1 text-sm text-[#1F1611]">
            {appointment.customer_name ?? t('publicBook.manage.guestFallback')}
          </p>

          <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-[#8B7E6E]">{t('publicBook.manage.services')}</p>
          <ul className="mt-1 space-y-1 text-sm text-[#1F1611]">
            {(appointment.services ?? []).map((line, index) => (
              <li key={`${line.name}-${index}`}>{line.name}</li>
            ))}
          </ul>

          <p className="mt-4 inline-flex rounded-full bg-[#F1EAD9] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#1F1611]">
            {appointment.status}
          </p>
        </div>

        {message ? (
          <p className="mt-4 rounded-2xl bg-green-50 px-4 py-3 text-sm text-green-800">{message}</p>
        ) : null}

        {!isCancelled && policies.can_reschedule ? (
          <form className="mt-6 space-y-3 rounded-3xl border border-[#ECE3D0] bg-[#FBF6EC] p-5" onSubmit={handleReschedule}>
            <h2 className="text-sm font-bold uppercase tracking-wide text-[#1F1611]">{t('publicBook.manage.reschedule')}</h2>
            <input
              type="datetime-local"
              value={rescheduleAt.replace(' ', 'T').slice(0, 16)}
              onChange={(event) => setRescheduleAt(event.target.value.replace('T', ' '))}
              className="w-full rounded-2xl border border-[#ECE3D0] bg-white px-4 py-3 text-sm"
              required
            />
            <button
              type="submit"
              disabled={busy === 'reschedule'}
              className="h-11 w-full rounded-full bg-[#1F1611] text-sm font-semibold text-white disabled:opacity-60"
            >
              {busy === 'reschedule' ? t('publicBook.manage.saving') : t('publicBook.manage.saveTime')}
            </button>
          </form>
        ) : null}

        {!isCancelled && policies.can_cancel ? (
          <button
            type="button"
            onClick={handleCancel}
            disabled={busy === 'cancel'}
            className="mt-4 h-11 w-full rounded-full border border-red-200 bg-red-50 text-sm font-semibold text-red-700 disabled:opacity-60"
          >
            {busy === 'cancel' ? t('publicBook.manage.cancelling') : t('publicBook.manage.cancelAppointment')}
          </button>
        ) : null}

        {!policies.can_cancel && !isCancelled ? (
          <p className="mt-4 text-center text-xs text-[#8B7E6E]">
            {t('publicBook.manage.changesClosed', { hours: policies.cancel_hours_before })}
          </p>
        ) : null}
      </div>
    </PublicBookShell>
  )
}
